import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';

import { hasVisibleWelcomeMessageContent } from './hasVisibleWelcomeMessageContent.ts';
import { transformData } from './transformWelcomeMessages.ts';
import { WelcomeMessagesQuery } from './WelcomeMessagesQuery.ts';
import type {
	WelcomeMessagesQuery$data,
	WelcomeMessagesQuery$variables,
} from './__generated__/WelcomeMessagesQuery.graphql.ts';

const SITE_ACTIVATION_DATE_TRAIT = 'confluence.ondemand.activationDate';
const ANNOUNCEMENTS_NEW_TENANT_CUTOFF = '2026-07-01T00:00:00Z';
const ANNOUNCEMENTS_NEW_TENANT_CUTOFF_MS = Date.parse(ANNOUNCEMENTS_NEW_TENANT_CUTOFF);

type SiteTraitAttribute = {
	name: string;
	value: unknown;
};

type SiteTraitsData = {
	attributes: ReadonlyArray<SiteTraitAttribute | null> | null;
} | null;

type AnnouncementsVisibilityInput = {
	welcomeMessageHtml?: string | null;
	isWelcomeMessageCustomized?: boolean | null;
	welcomeMessageLoading: boolean;
	welcomeMessageError?: unknown;
	siteTraitsData: SiteTraitsData;
	siteTraitsLoading: boolean;
	siteTraitsError?: unknown;
};

const getSiteTraitValue = (siteTraitsData: SiteTraitsData, traitName: string): unknown =>
	siteTraitsData?.attributes?.find((attribute) => attribute?.name === traitName)?.value;

const isTenantCreatedAtOrAfterAnnouncementsCutoff = (activationDate: unknown): boolean => {
	if (typeof activationDate !== 'string') {
		return false;
	}

	const activationDateMs = Date.parse(activationDate);

	if (Number.isNaN(activationDateMs)) {
		return false;
	}

	return activationDateMs >= ANNOUNCEMENTS_NEW_TENANT_CUTOFF_MS;
};

export const getAnnouncementsPanelVisibility = ({
	welcomeMessageHtml,
	isWelcomeMessageCustomized,
	welcomeMessageLoading,
	welcomeMessageError,
	siteTraitsData,
	siteTraitsLoading,
	siteTraitsError,
}: AnnouncementsVisibilityInput): {
	loading: boolean;
	shouldShowAnnouncementsPanel: boolean;
} => {
	// Wait until both the welcome-message query and the site-trait fetch have settled.
	// `useSiteTraits` reports `loading: false` with `data: null` on the very first render
	// because it starts its fetch inside an effect, so a bare `siteTraitsLoading` check is not
	// enough: treat "no data and no error yet" as still-resolving. This keeps the SSR output
	// and the first client render on the same branch (tab omitted) and prevents the
	// Announcements tab from briefly flashing for a new tenant before the activation-date
	// trait arrives. Once the traits resolve, a missing/invalid/errored trait fails open below.
	const siteTraitsResolved = Boolean(siteTraitsData) || Boolean(siteTraitsError);

	if (welcomeMessageLoading || siteTraitsLoading || !siteTraitsResolved) {
		return {
			loading: true,
			shouldShowAnnouncementsPanel: false,
		};
	}

	const activationDate = getSiteTraitValue(siteTraitsData, SITE_ACTIVATION_DATE_TRAIT);

	// Hide for tenants created at/after the cutoff.
	if (!siteTraitsError && isTenantCreatedAtOrAfterAnnouncementsCutoff(activationDate)) {
		return {
			loading: false,
			shouldShowAnnouncementsPanel: false,
		};
	}

	// Welcome-message query errors default to keeping the announcements tab.
	if (welcomeMessageError) {
		return {
			loading: false,
			shouldShowAnnouncementsPanel: true,
		};
	}

	// The announcement should not be shown if it is the default message.
	if (!isWelcomeMessageCustomized) {
		return {
			loading: false,
			shouldShowAnnouncementsPanel: false,
		};
	}

	// Empty-content rule still applies even when tenant age is unknown or the trait errored.
	if (!hasVisibleWelcomeMessageContent(welcomeMessageHtml)) {
		return {
			loading: false,
			shouldShowAnnouncementsPanel: false,
		};
	}

	// Else, default to showing the announcements panel.
	return {
		loading: false,
		shouldShowAnnouncementsPanel: true,
	};
};

export const useAnnouncementsPanelVisibility = (): {
	loading: boolean;
	shouldShowAnnouncementsPanel: boolean;
} => {
	const {
		data: welcomeMessagesData,
		loading: welcomeMessageLoading,
		error: welcomeMessageError,
	} = useQueryInterop<WelcomeMessagesQuery$data, WelcomeMessagesQuery$variables>(
		WelcomeMessagesQuery,
		{
			fetchPolicy: 'cache-first',
		},
	);
	const {
		data: siteTraitsData,
		loading: siteTraitsLoading,
		error: siteTraitsError,
	} = useSiteTraits();

	// The welcome-message GraphQL error is handled intentionally below (we fail open and keep the
	// Announcements tab), so mark it handled to stop the GraphQL error processor from reporting it as
	// unhandled.
	if (welcomeMessageError) {
		markErrorAsHandled(welcomeMessageError);
	}

	const { welcomeMessageHtml, isWelcomeMessageCustomized } = transformData(welcomeMessagesData);

	return getAnnouncementsPanelVisibility({
		welcomeMessageHtml,
		isWelcomeMessageCustomized,
		welcomeMessageLoading,
		welcomeMessageError,
		siteTraitsData,
		siteTraitsLoading,
		siteTraitsError,
	});
};
